import React, {
  createContext,
  useContext,
  useEffect,
  useRef,
  useState,
} from 'react';
import { Platform } from 'react-native';

import {
  LocalFile,
  Document,
  DocumentDetailFragmentDoc,
  useDeleteDirectUploadsMutation,
} from '@graphql/generated';
import { useApolloClient } from '@hooks/useApolloClient';
import useMe from '@hooks/useMe';
import { MIXPANEL_EVENTS } from '@src/constants/constants';
import { useMixpanel } from '@src/utils/mixpanel';
import {
  calculateUploadTimeoutMs,
  computeFileHash,
  mapLocalFilesToDocuments,
} from '@utils/fileUtils';
import { AsyncStorage } from '@utils/storage';
import { clearBlobRecoveryRetries } from '@utils/uploadRecovery';

import useFileProcessor from './useFileProcessor';

import type { UploadProgressData } from 'expo-file-system/legacy';

type UploadingFile = {
  file: LocalFile;
  progress?: number;
  compressionProgress?: number;
  status?: 'compressing' | 'uploading' | 'success' | 'error';
};

const HASH_UPLOAD_MAP_KEY = 'hashUploadMap';

type ChatUploadManagerContextType = {
  uploadingFiles: UploadingFile[];
  setUploadingFiles: React.Dispatch<React.SetStateAction<UploadingFile[]>>;
  directUploadFiles: (files: LocalFile[]) => Promise<
    {
      blobId: string;
      clientId: string;
      url: string;
    }[]
  >;
  retryUpload: (file: LocalFile) => void;
  invalidateUploadedBlob: (files: LocalFile[]) => Promise<void>;
  waitUntilNotUploading: (files: LocalFile[]) => Promise<void>;
  // Round 8/9 review: the implementation has always been async (hashes the
  // file, may await a delete mutation, then clears upload state) -- this
  // signature just never reflected that, until MyChatForm.tsx needed to
  // chain .catch() onto it.
  removeUploadingFile: (file: LocalFile) => Promise<void>;
  finalizeUploadedFiles: (files: LocalFile[]) => void;
  updateCompressionProgress: (clientId: string, progress: number) => void;
};

const ChatUploadManagerContext =
  createContext<ChatUploadManagerContextType | null>(null);

export const processUploadResults = (
  response: {
    blobId: string;
    clientId: string;
    numericBlobId: string;
  }[],
  hashMap: Map<string, string>,
  hashUploadMap: React.RefObject<
    Map<
      string,
      {
        blobId: string;
        refCount: number;
        numericBlobId: string;
      }
    >
  >
) => {
  response.forEach((uploadResult) => {
    const clientId = uploadResult?.clientId;
    const hash = hashMap.get(clientId);

    if (hash) {
      if (!hashUploadMap.current) {
        hashUploadMap.current = new Map();
      }
      hashUploadMap.current.set(hash, {
        blobId: uploadResult?.blobId,
        refCount: 0,
        numericBlobId: uploadResult?.numericBlobId,
      });
    } else {
      console.warn(`No hash found for clientId: ${clientId}`);
    }
  });
};

/**
 * TA-10885 (Copilot review): the initial loadHashUploadMap() read can resolve
 * AFTER a recovery has already deleted an entry and written the map through.
 * Assigning the stored copy then resurrects the dead blobId moments before the
 * retry reuses it — the exact bug this PR fixes, reintroduced by a race. Once
 * the in-memory map has been written, it is the newer copy: keep it.
 */
export const shouldApplyLoadedHashMap = (
  loadedMapSize: number,
  hasBeenWritten: boolean
): boolean => loadedMapSize > 0 && !hasBeenWritten;

let RNFS: typeof import('react-native-fs');
if (Platform.OS !== 'web') {
  RNFS = require('react-native-fs');
}

// Resolves '.' and '..' segments the way a filesystem call would, so
// containment can be checked against the path unlink will actually touch,
// not the raw string. Pure POSIX segment resolution (no symlink following --
// RNFS/the native layer has no JS-reachable realpath, and every path this
// app itself ever writes is symlink-free) -- exported for direct testing
// (Copilot review, PR #5958 round 8).
export const canonicalizePosixPath = (path: string): string => {
  const isAbsolute = path.startsWith('/');
  const resolved: string[] = [];
  for (const segment of path.split('/')) {
    if (segment === '' || segment === '.') continue;
    if (segment === '..') {
      resolved.pop();
    } else {
      resolved.push(segment);
    }
  }
  return (isAbsolute ? '/' : '') + resolved.join('/');
};

// True only when `url` is a file:// path that, once percent-decoded and
// '.'/'..' segments are resolved, actually names a DESCENDANT of one of
// this app's own sandboxed directories -- a plain string startsWith on the
// raw URL lets a crafted (or just malformed) value like
// file:///documents/../shared/other-app.mp4 pass the prefix check while the
// native unlink call resolves '..' and deletes outside the allowlisted
// directory entirely (Copilot review, PR #5958 round 8). Deliberately
// excludes equality with the directory itself -- RNFS.unlink accepts
// directories too, so a malformed `url` equal to the bare directory (e.g.
// file:///documents, or a trailing-slash equivalent, which canonicalizes to
// the same bare form) must never be treated as an acceptable file to clean
// up, or cleanup could remove this app's entire storage directory instead
// of one file (Copilot review, PR #5958 round 9). Decodes percent-escapes
// (e.g. %2e%2e for ..) before canonicalizing -- comparing the raw, still-
// encoded string lets an encoded traversal segment pass this check while
// native file-URL handling decodes it before stat/unlink; a malformed
// escape sequence fails closed rather than being treated as own-cache
// (Copilot review, PR #5958 round 11). Exported for direct testing.
export const isWithinOwnCacheDirectory = (
  url: string,
  ownDirs: string[]
): boolean => {
  if (!url.startsWith('file://')) return false;
  let decodedPath: string;
  try {
    decodedPath = decodeURIComponent(url.slice('file://'.length));
  } catch {
    return false;
  }
  const canonicalPath = canonicalizePosixPath(decodedPath);
  return ownDirs.some((dir) =>
    canonicalPath.startsWith(`${canonicalizePosixPath(dir)}/`)
  );
};

export const clearUploadUIAndCache = async (
  files: LocalFile[],
  setUploadingFiles: React.Dispatch<React.SetStateAction<UploadingFile[]>>
) => {
  const clientIdsToRemove = new Set(files.map((item) => item.clientId));

  setUploadingFiles((prev) =>
    prev.filter((item) => !clientIdsToRemove.has(item.file.clientId))
  );

  if (Platform.OS !== 'web') {
    // Copilot review, PR #5958: a file whose own cache copy failed (e.g.
    // ShareIntentToChat.tsx / selectFiles.ts's copy-failure fallback) has
    // `url` pointing at the original, caller-owned source -- a content://
    // URI or a file:// path outside this app's own storage -- not a copy
    // this app made. Unlinking that unconditionally risks deleting a file
    // the user never asked us to delete (their gallery video, a file from
    // another app's share sheet). Only ever unlink paths this app actually
    // wrote under one of its own sandboxed directories -- not just
    // DocumentDirectoryPath: ImageMarkup.tsx's ViewShot capture writes
    // under the OS temp/cache dir instead (never copied into
    // DocumentDirectoryPath when compressImage skips an already-small
    // file), so a DocumentDirectoryPath-only allowlist silently leaked
    // those captures on disk (Alvin review, PR #5958). isWithinOwnCacheDirectory
    // canonicalizes '.'/'..' segments before the containment check -- a raw
    // startsWith would let file:///documents/../shared/other-app.mp4 pass
    // while the native unlink call resolves '..' and deletes outside the
    // allowlisted directory entirely (Copilot review, PR #5958 round 8).
    const ownCacheDirs = [
      RNFS.DocumentDirectoryPath,
      RNFS.CachesDirectoryPath,
      RNFS.TemporaryDirectoryPath,
    ];
    for (const file of files) {
      try {
        if (file?.url && isWithinOwnCacheDirectory(file.url, ownCacheDirs)) {
          // RNFS.unlink removes directories recursively, and any descendant
          // path -- not just the allowlisted root itself -- passes the
          // containment check above (e.g. file:///documents/subdir, if a
          // subdirectory ever exists there). A malformed LocalFile.url could
          // therefore still target a real directory and wipe out everything
          // under it. Confirm the target is actually a regular file
          // immediately before unlinking (Copilot review, PR #5958 round 10).
          const stat = await RNFS.stat(file.url);
          if (stat.isFile()) {
            await RNFS.unlink(file.url);
          }
        }
      } catch (deleteError) {
        console.error(`Failed to delete file: ${file.url}`, deleteError);
      }
    }
  }
};

export const ChatUploadManagerProvider: React.FC<{
  children: React.ReactNode;
}> = ({ children }) => {
  const { processFiles } = useFileProcessor();
  const { me } = useMe();
  const { trackEvent } = useMixpanel();

  const [uploadingFiles, setUploadingFiles] = useState<UploadingFile[]>([]);
  const uploadingFilesRef = useRef<UploadingFile[]>([]);
  const hashUploadMap = useRef(
    new Map<
      string,
      { blobId: string; refCount: number; numericBlobId: string }
    >()
  );

  const [deleteDirectUpload] = useDeleteDirectUploadsMutation();

  useEffect(() => {
    uploadingFilesRef.current = uploadingFiles;
  }, [uploadingFiles]);

  // Flipped by persistHashUploadMap, so every mutation path (upload results,
  // deleteHashEntry, invalidateUploadedBlob, finalizeUploadedFiles) marks the
  // in-memory map as the newer copy — see shouldApplyLoadedHashMap.
  const hashUploadMapWrittenRef = useRef(false);

  useEffect(() => {
    loadHashUploadMap().then((loadedMap) => {
      if (
        shouldApplyLoadedHashMap(
          loadedMap.size,
          hashUploadMapWrittenRef.current
        )
      ) {
        hashUploadMap.current = loadedMap;
      } else {
        console.warn('Loaded empty or superseded map, keeping current');
      }
    });
  }, []);

  const previousUserIdRef = useRef<string | undefined>(me?.id);

  useEffect(() => {
    const currentUserId = me?.id;

    if (currentUserId !== previousUserIdRef.current) {
      if (previousUserIdRef.current) {
        hashUploadMap.current.clear();
        AsyncStorage.deleteItem(HASH_UPLOAD_MAP_KEY);
        // TA-10885: the recovery budget is keyed on message clientIds and is
        // paired with this map — a new session starts with both empty.
        clearBlobRecoveryRetries();
      }
      previousUserIdRef.current = currentUserId;
    }
  }, [me?.id]);

  const loadHashUploadMap = async (): Promise<
    Map<string, { blobId: string; refCount: number; numericBlobId: string }>
  > => {
    try {
      const stored = await AsyncStorage.getItem(HASH_UPLOAD_MAP_KEY);
      if (!stored) return new Map();
      const parsed = JSON.parse(stored);
      return new Map(parsed);
    } catch (err) {
      console.error('Failed to load hashUploadMap', err);
      return new Map();
    }
  };

  const persistHashUploadMap = async (
    map: Map<
      string,
      { blobId: string; refCount: number; numericBlobId: string }
    >
  ): Promise<void> => {
    hashUploadMapWrittenRef.current = true;
    try {
      // TA-10885: an emptied map must reach disk too. Skipping the write when
      // the last entry is deleted left the stale copy on disk, so the next
      // launch restored a hash → blobId pair for a blob that had already been
      // deleted server-side and the file was never re-uploaded.
      await AsyncStorage.setItem(
        HASH_UPLOAD_MAP_KEY,
        JSON.stringify(Array.from(map.entries()))
      );
    } catch (err) {
      console.error('Failed to persist hashUploadMap', err);
    }
  };

  const deleteHashEntry = async (fileHash: string) => {
    hashUploadMap.current.delete(fileHash);
    persistHashUploadMap(hashUploadMap.current);
  };

  const directUploadFiles = async (
    newLocalFilesToUpload: LocalFile[]
  ): Promise<{ blobId: string; clientId: string; url: string }[]> => {
    const documents = mapLocalFilesToDocuments(newLocalFilesToUpload, me!);

    const prevMap = new Map(uploadingFiles.map((f) => [f.file.clientId, f]));

    const alreadyUploaded: {
      blobId: string;
      clientId: string;
      url: string;
    }[] = [];
    const toUploadDocs: Document[] = [];
    const hashMap = new Map<string, string>();
    const newUploadingEntries: UploadingFile[] = [];

    for (const doc of documents) {
      const file = newLocalFilesToUpload.find(
        (f) => f.clientId === doc.clientId
      );
      if (!file) continue;
      const fileHash = await computeFileHash(file.url ?? file.path);
      if (fileHash == '') {
        continue;
      }
      hashMap.set(doc.clientId, fileHash);
      const existingClient = prevMap.get(doc.clientId);
      const existingHash = hashUploadMap.current.get(fileHash);

      if (existingClient?.status === 'success' && existingClient.file.blobId) {
        alreadyUploaded.push({
          blobId: existingClient.file.blobId,
          clientId: doc.clientId,
          url: doc.file.url,
        });
        newUploadingEntries.push({
          file,
          status: 'success',
          progress: 1,
        });
      } else if (existingHash?.blobId) {
        alreadyUploaded.push({
          blobId: existingHash.blobId,
          clientId: doc.clientId,
          url: doc.file.url,
        });
        newUploadingEntries.push({
          file,
          status: 'success',
          progress: 1,
        });
        updateDocumentCache(
          [doc],
          [
            {
              blobId: existingHash.blobId,
              clientId: doc.clientId,
            },
          ]
        );
      } else {
        toUploadDocs.push(doc);
        newUploadingEntries.push({
          file,
          status: 'uploading',
          progress: 0,
        });
      }
    }

    setUploadingFiles((prev) => {
      const map = new Map(prev.map((f) => [f.file.clientId, f]));
      newUploadingEntries.forEach((f) => {
        map.set(f.file.clientId, f);
      });
      return Array.from(map.values());
    });

    if (toUploadDocs.length === 0) {
      return alreadyUploaded;
    }

    const uploadIds = new Set(toUploadDocs.map((d) => d.clientId));

    try {
      const response = await processFiles(toUploadDocs, uploadCallback, false);
      processUploadResults(response, hashMap, hashUploadMap);
      await persistHashUploadMap(hashUploadMap.current);

      setUploadingFiles((prev) =>
        prev.map((item) => {
          if (!uploadIds.has(item.file.clientId)) return item;

          const uploadResult = response.find(
            (r) => r.clientId === item.file.clientId
          );

          return {
            ...item,
            file: {
              ...item.file,
              blobId: uploadResult?.blobId ?? '',
            },
            status: 'success',
            progress: 1,
          };
        })
      );

      updateDocumentCache(toUploadDocs, response);

      return [...alreadyUploaded, ...response];
    } catch (err) {
      setUploadingFiles((prev) =>
        prev.map((item) =>
          uploadIds.has(item.file.clientId)
            ? { ...item, status: 'error' }
            : item
        )
      );

      if (alreadyUploaded.length > 0) {
        return [...alreadyUploaded];
      }

      throw err;
    }
  };

  const { client } = useApolloClient();
  const { cache } = client || {};

  const updateDocumentCache = (
    documents: Document[],
    response: {
      blobId: string;
      clientId: string;
    }[]
  ) => {
    if (!cache) return undefined;
    documents.forEach((item) => {
      const uploadResult = response.find((r) => r.clientId === item.clientId);
      const newDocument = {
        id: cache.identify(item),
        fragment: DocumentDetailFragmentDoc,
        fragmentName: 'DocumentDetail',
        data: { ...item, blobId: uploadResult?.blobId },
      };
      cache.writeFragment(newDocument);
    });
  };

  let lastUpdateTime = 0;

  const uploadCallback = (
    documentClientId: string,
    uploadProgress: UploadProgressData,
    blobId?: string
  ) => {
    const now = Date.now();
    if (now - lastUpdateTime < 100) return;
    lastUpdateTime = now;

    const progress =
      uploadProgress.totalBytesSent / uploadProgress.totalBytesExpectedToSend;
    setUploadingFiles((prev) =>
      prev.map((item) =>
        item.file.clientId == documentClientId
          ? {
              ...item,
              file: {
                ...item.file,
                blobId: progress >= 1 ? blobId : '',
              },
              progress:
                progress > (item.progress ?? 0) ? progress : item.progress,
              status: 'uploading',
            }
          : item
      )
    );
    if (blobId && progress >= 1) {
      const localFiles = uploadingFiles.filter(
        (item) => item.file.clientId == documentClientId
      );
      if (localFiles.length > 0) {
        const documents = mapLocalFilesToDocuments([localFiles[0].file], me!);
        updateDocumentCache(documents, [
          { blobId: blobId, clientId: documentClientId },
        ]);
      }
    }
  };

  const retryUpload = async (file: LocalFile) => {
    const clientId = file.clientId;

    setUploadingFiles((prev) =>
      prev.filter((item) => item.file.clientId !== clientId)
    );
    // TA-7175: the retry affordance is tapped precisely when the previous
    // attempt failed, so a second failure (still offline) is the common case.
    // directUploadFiles rethrows once nothing in the batch uploaded, and the
    // callers are bare onPress handlers -- leaving this floating turned every
    // failed retry into an unhandled promise rejection. Not reported as a
    // non-fatal either: "retried while still offline" is an expected state, not
    // a defect worth a Sentry event per tap.
    await directUploadFiles([file]).catch(() => {});
    // directUploadFiles only puts the entry back on 'error' for a processFiles
    // failure. A hash/mapping rejection before its try, or an empty hash
    // (skipped), leaves the entry removed above, so the file would vanish with
    // no retry affordance. Re-add it as 'error' whenever it is missing.
    setUploadingFiles((prev) =>
      prev.some((item) => item.file.clientId === clientId)
        ? prev
        : [...prev, { file, status: 'error', progress: 0 }]
    );
  };

  const waitUntilNotUploading = async (files: LocalFile[]): Promise<void> => {
    const totalSize = files.reduce((sum, f) => sum + (f.size ?? 0), 0);
    const sizeInMB = totalSize / (1024 * 1024);
    // Timeout budget covers both the compression phase and the network upload;
    // shares its formula with the network-upload-task timeout in
    // uploadFilesAsyncNative (fileUtils.ts) so the two can't drift apart again.
    const timeoutDuration = calculateUploadTimeoutMs(sizeInMB); // ms
    const targetClientIds = new Set(files.map((f) => f.clientId));
    const start = Date.now();

    await new Promise<void>((resolve, reject) => {
      const interval = setInterval(() => {
        const isUploading = uploadingFilesRef.current.some(
          (item) =>
            targetClientIds.has(item.file.clientId) &&
            (item.status === 'uploading' || item.status === 'compressing')
        );

        const expired = Date.now() - start > timeoutDuration;

        if (expired) {
          clearInterval(interval);
          trackEvent(MIXPANEL_EVENTS.UI_INTERACTION_TIMEOUT, {
            interaction_type: 'file_upload',
            timeout_seconds: timeoutDuration / 1000,
            client_ids: Array.from(targetClientIds),
          });
          reject(new Error('Timeout while waiting for uploads to finish'));
        } else if (!isUploading) {
          clearInterval(interval);
          resolve();
        }
      }, 300);
    });
  };

  /**
   * TA-10885: forget the hash → blobId dedup entry for these files without
   * touching the files themselves, so the next directUploadFiles really
   * uploads instead of handing back a blobId the server no longer has. The
   * uploading entries go too: a 'success' entry is consulted ahead of the hash
   * map and would reinstate the same dead blobId, and its spinner is what the
   * user sees hang after the send fails.
   */
  const invalidateUploadedBlob = async (files: LocalFile[]) => {
    for (const file of files) {
      const fileHash = await computeFileHash(file.url ?? file.path);
      if (!fileHash) continue;
      hashUploadMap.current.delete(fileHash);
    }
    await persistHashUploadMap(hashUploadMap.current);

    const clientIds = new Set(files.map((file) => file.clientId));
    setUploadingFiles((prev) =>
      prev.filter((item) => !clientIds.has(item.file.clientId))
    );
  };

  const removeUploadingFile = async (file: LocalFile) => {
    // AdrianKao review, PR #5914: clearUploadUIAndCache -- the call that
    // actually clears the "compressing" entry and unlinks the cached file --
    // used to run only after a successful hash. An unreadable content://
    // URI (a rejected video whose selectFiles.ts cache copy failed) could
    // make computeFileHash reject, leaving this stuck forever. Run cleanup
    // in a finally so it always happens, whatever the hash outcome.
    //
    // TA-10885 (main): separately, computeFileHash can legitimately resolve
    // '' for a file that's already gone from disk -- that only rules out
    // the blob cleanup below, not the UI entry, which still has to go or
    // the removed attachment keeps its thumbnail.
    try {
      const fileHash = await computeFileHash(file.url ?? file.path);

      const entry = fileHash ? hashUploadMap.current.get(fileHash) : undefined;
      if (entry && fileHash) {
        const { refCount = 0, numericBlobId } = entry;
        if (numericBlobId && refCount === 0) {
          try {
            await deleteDirectUpload({
              variables: { blobId: numericBlobId },
            });
            deleteHashEntry(fileHash);
            if (__DEV__) {
              console.log(`Deleted blob ${numericBlobId}`);
            }
          } catch (e) {
            console.error(`Failed to delete blob ${numericBlobId}`, e);
          }
        }
      }
    } finally {
      await clearUploadUIAndCache([file], setUploadingFiles);
    }
  };

  const finalizeUploadedFiles = async (files: LocalFile[]) => {
    for (const file of files) {
      const fileHash = await computeFileHash(file.url ?? file.path);
      if (!fileHash) continue;

      const entry = hashUploadMap.current.get(fileHash);
      if (entry) {
        entry.refCount = (entry.refCount ?? 0) + 1;
        hashUploadMap.current.set(fileHash, entry);
        await persistHashUploadMap(hashUploadMap.current);
      }
    }

    await clearUploadUIAndCache(files, setUploadingFiles);
  };

  const compressionUpdateTimeRef = useRef(0);

  const updateCompressionProgress = (clientId: string, progress: number) => {
    const now = Date.now();
    if (now - compressionUpdateTimeRef.current < 100 && progress < 1) return;
    compressionUpdateTimeRef.current = now;

    setUploadingFiles((prev) =>
      prev.map((item) =>
        item.file.clientId === clientId
          ? {
              ...item,
              compressionProgress: Math.max(
                progress,
                item.compressionProgress ?? 0
              ),
              status: progress >= 1 ? 'uploading' : 'compressing',
            }
          : item
      )
    );
  };

  return (
    <ChatUploadManagerContext.Provider
      value={{
        uploadingFiles,
        setUploadingFiles,
        directUploadFiles,
        retryUpload,
        invalidateUploadedBlob,
        waitUntilNotUploading,
        removeUploadingFile,
        finalizeUploadedFiles,
        updateCompressionProgress,
      }}>
      {children}
    </ChatUploadManagerContext.Provider>
  );
};

export const useChatUploadManager = () => {
  const context = useContext(ChatUploadManagerContext);
  if (!context)
    throw new Error(
      'useChatUploadManager must be used within ChatUploadManagerProvider'
    );
  return context;
};

/**
 * Non-throwing variant, for a component whose contract is to render nothing
 * when its providers are missing rather than to crash the tree — see
 * TaskCreateAttachmentsPreview (Copilot review). Callers must handle `null`.
 */
export const useOptionalChatUploadManager = () =>
  useContext(ChatUploadManagerContext);
